import { ApiResponse } from '@models/common/api';
import { EPublisherName } from '@models/listing/listing';
import {
  IFindAvailableRequest,
  IFindConnectedAccountsRequest,
  IMatchLocationRequest,
  IPatchToggleSyncRequest,
  IUnmatchLocationRequest,
  IWebHookConnectAccountRequest,
} from '@models/listing/request';
import {
  IConnectAccountResponse,
  IFindAvailableResponse,
  IFindConnectedAccountsResponse,
  IFindListingsResponse,
  IListingAnalyticsResponse,
  IMatchLocationResponse,
  IPatchToggleSync,
  IVoiceAssistant,
  IWebHookConnectAccountResponse,
} from '@models/listing/response';
import { IAnalyticsRequest } from '@models/reviews/requests';

import { get, post, patch } from './methods';

const BASE_ENDPOINT = 'listing';

const callbackUrlBase = `${window.location.origin}/app/org-settings/connect`;

function buildConnectAccountWebhookUrl({
  publisher,
  organizationId,
  locationId,
  shouldFirstRun = false,
}: {
  publisher: string;
  organizationId: number;
  locationId: number;
  shouldFirstRun?: boolean;
}) {
  return `${callbackUrlBase}?locationId=${locationId}&organizationId=${organizationId}&shouldFirstRun=${shouldFirstRun}&publisher=${publisher}`;
}

const listingAPI = {
  connectAccount: async (
    publisherName: EPublisherName,
    locationId: number,
    organizationId: number,
    shouldFirstRun = false,
  ) => {
    if (publisherName === EPublisherName.GOOGLE)
      return listingAPI.connectGoogle(locationId, organizationId, shouldFirstRun);
    else if (publisherName === EPublisherName.FACEBOOK)
      return listingAPI.connectFacebook(locationId, organizationId, shouldFirstRun);
    else throw new Error('Try to connect not recognized publisher');
  },
  connectGoogle: async (locationId: number, organizationId: number, shouldFirstRun: boolean = false) => {
    const connectionUrl = buildConnectAccountWebhookUrl({
      publisher: 'google',
      locationId,
      organizationId,
      shouldFirstRun,
    });
    // listing-review checks these ids against the caller's account (Clerk principal); without them it returns 400.
    const body = {
      locationId,
      organizationId,
      errorUrl: connectionUrl,
      successUrl: connectionUrl,
    };
    const response = await post<ApiResponse<IConnectAccountResponse>>(`${BASE_ENDPOINT}/connect-google`, body);
    return response.data;
  },
  connectFacebook: async (locationId: number, organizationId: number, shouldFirstRun: boolean = false) => {
    const connectionUrl = buildConnectAccountWebhookUrl({
      publisher: 'facebook',
      locationId,
      organizationId,
      shouldFirstRun,
    });
    const body = {
      locationId,
      organizationId,
      errorUrl: connectionUrl,
      successUrl: connectionUrl,
    };
    const response = await post<ApiResponse<IConnectAccountResponse>>(`${BASE_ENDPOINT}/connect-facebook`, body);
    return response.data;
  },
  webHookConnectAccount: async (body: IWebHookConnectAccountRequest) => {
    // Mock response for local testing with mock connectedAccountId
    if (body.connectedAccountId.startsWith('mock-account-') || body.connectedAccountId.startsWith('mock-fb-')) {
      console.warn('Using mock webHookConnectAccount for localhost testing');

      return Promise.resolve({
        success: true,
        data: {
          id: Math.floor(Math.random() * 1000),
          synupConnectedAccountId: body.connectedAccountId,
          email: body.publisher === EPublisherName.FACEBOOK ? 'test@facebook.com' : 'test@example.com',
          publisher: body.publisher,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
        },
        message: 'Mock connected account saved',
      });
    }

    const response = await post<ApiResponse<IWebHookConnectAccountResponse>>(
      `${BASE_ENDPOINT}/connected-accounts`,
      body,
    );
    return response.data;
  },
  findConnectedAccounts: async (params: IFindConnectedAccountsRequest) => {
    const response = await get<ApiResponse<IFindConnectedAccountsResponse>>(`${BASE_ENDPOINT}/connected-accounts`, {
      params,
    });
    return response.data;
  },
  findListing: async (locationId: number) => {
    const params = { locationId };
    const response = await get<ApiResponse<IFindListingsResponse>>(BASE_ENDPOINT, { params });
    return response.data;
  },
  findAvailable: async (params: IFindAvailableRequest) => {
    const response = await get<ApiResponse<IFindAvailableResponse>>(`${BASE_ENDPOINT}/available`, { params });
    return response.data;
  },
  matchLocation: async (body: IMatchLocationRequest) => {
    // Mock response for local testing
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.warn('Using mock matchLocation for localhost testing');

      return Promise.resolve({
        success: true,
        data: {
          id: body.locationId,
          listingUrl: `https://www.google.com/maps/place/?cid=${body.listingId}`,
          publisher: 'GOOGLE',
          synced: true,
        },
        message: 'Mock location matched successfully',
      });
    }

    const response = await post<ApiResponse<IMatchLocationResponse>>(`${BASE_ENDPOINT}/match-location`, body);
    return response.data;
  },
  unmatchLocation: async (body: IUnmatchLocationRequest) => {
    const response = await post<ApiResponse<IMatchLocationResponse>>(`${BASE_ENDPOINT}/unmatch-location`, body);
    return response.data;
  },
  getAnalytics: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IListingAnalyticsResponse>>(`${BASE_ENDPOINT}/analytics`, { params });
    return response.data;
  },
  toggleSync: async (body: IPatchToggleSyncRequest) => {
    const response = await patch<ApiResponse<IPatchToggleSync>>(`${BASE_ENDPOINT}/toggle-sync`, body);
    return response.data;
  },
  findVoiceAssistants: async (locationId: number) => {
    const response = await get<ApiResponse<IVoiceAssistant[]>>(`${BASE_ENDPOINT}/voice-assistants`, {
      params: { locationId },
    });
    return response.data;
  },

  // Spartacus-specific connection (custom callback URL)
  connectGoogleSpartacus: async (brandId: number, organizationId: number) => {
    // Mock response for local testing
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.warn('Using mock GMB connection for localhost testing');

      // Simulate opening Synup auth and immediately returning with success
      // In real flow, user would authenticate with Google and Synup would redirect back
      const mockConnectedAccountId = `mock-account-${Date.now()}`;
      const mockEmail = 'test@example.com';

      // Return a mock URL that simulates the Synup auth page
      // When opened, it will immediately redirect back with connection params
      const mockAuthUrl = `${window.location.origin}/spartacus/app/your-location?connectedAccountId=${mockConnectedAccountId}&accountMail=${mockEmail}&brandId=${brandId}&organizationId=${organizationId}&publisher=google`;

      return Promise.resolve({
        success: true,
        data: { url: mockAuthUrl },
        message: 'Mock connection URL for local testing',
      });
    }

    const callbackUrl = `${window.location.origin}/spartacus/app/your-location?brandId=${brandId}&organizationId=${organizationId}&publisher=google`;

    const body = {
      errorUrl: callbackUrl,
      successUrl: callbackUrl,
    };
    const response = await post<ApiResponse<IConnectAccountResponse>>(`${BASE_ENDPOINT}/connect-google`, body);
    return response.data;
  },
};
export default listingAPI;
